/**
 * useFcmTokenReconciliation Hook
 *
 * Silently keeps the server-side FCM token in sync for users who have already
 * opted into push notifications.
 *
 * Why this exists:
 * FCM web tokens rotate (browser/OS housekeeping, PWA reinstall, push-service
 * changes). The app only mints a token via the explicit enable toggle, so a
 * rotated token would die while `pushNotificationsEnabled` stays true — the UI
 * shows "enabled," the enable prompt stays hidden, and the send-path cleanup
 * quietly empties `fcmTokens`, leaving the account permanently silent.
 *
 * The Firebase modular SDK has no `onTokenRefresh`; the supported pattern is to
 * call `getToken()` on load, which returns the current token (minting a fresh
 * one if the old rotated). This hook does exactly that, once per logged-in user
 * per session, and only writes when the token has actually changed.
 *
 * It is intentionally silent: it NEVER requests notification permission. If
 * permission isn't granted (or token acquisition fails for any other reason),
 * `getFCMToken` throws and the catch below swallows it — we do nothing.
 */

"use client";

import { saveFCMToken } from "@/actions/push-notification";
import { useAuth } from "@/contexts/AuthContext";
import { getFCMToken, getPushSupportStatus } from "@/lib/fcm";
import { useEffect, useRef } from "react";

const VAPID_KEY = process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY || "";

/** Per-user localStorage key holding the last token synced from this device. */
const syncKey = (userId: string) => `fcmTokenSynced:${userId}`;

export function useFcmTokenReconciliation(): void {
  const { user } = useAuth();

  // Tracks which user id we've already reconciled in this session so the effect
  // doesn't re-run on every render, while still re-running if the user changes.
  const reconciledFor = useRef<string | null>(null);

  useEffect(() => {
    if (!user?.id || !user.pushNotificationsEnabled) return;
    if (reconciledFor.current === user.id) return;

    // Cheap front gate: bail on unsupported platforms / iOS-not-installed-as-PWA
    // before touching the messaging SDK or service worker.
    if (getPushSupportStatus() !== "supported") return;
    if (!VAPID_KEY) return;

    // Mark before the async work so a re-render (or StrictMode double-invoke)
    // doesn't kick off a second reconcile for the same user.
    reconciledFor.current = user.id;

    const userId = user.id;

    void (async () => {
      try {
        // getFCMToken checks notification permission and registers the service
        // worker internally; it throws (without prompting) if permission isn't
        // granted or a token can't be obtained — caught and ignored below.
        const token = await getFCMToken(VAPID_KEY);

        // Skip the Firestore write if this device already synced this exact
        // token — keeps it to ~one write per device per actual rotation.
        if (localStorage.getItem(syncKey(userId)) === token) return;

        const result = await saveFCMToken({ token });
        if (result.success) {
          localStorage.setItem(syncKey(userId), token);
        }
      } catch {
        // Reconciliation is best-effort and must never surface to the UI.
      }
    })();
  }, [user?.id, user?.pushNotificationsEnabled]);
}
